import { Autocomplete, Grid, Typography } from "@mui/material";
import { useTranslation } from "react-i18next";

import FormSection from "../../../../common/components/functional/formSection/FormSection";
import { useAppDispatch, useAppSelector } from "../../../../app/hooks";
import { useEffect, useMemo, useState } from "react";
import { editEmpAnnualLeavePaymentRule } from "../../../employment/employmentSlice";
import { Controller, FieldValues, useForm } from "react-hook-form";
import { isFulfilled } from "@reduxjs/toolkit";
import { Employment } from "../../../employment/employmentApi";
import isEmpty from "../../../../common/helpers/isEmpty";
import CommonTextField from "../../../../common/components/functional/commonTextField/CommonTextField";

type Props = {
  employment?: Employment;
  canEdit: boolean;
};

export const AnnualLeave = ({ employment, canEdit }: Props) => {
  const [edit, setEdit] = useState(false);
  const dispatch = useAppDispatch();

  const { t } = useTranslation("talentProfile");
  const paymentRules = useAppSelector(
    (state) => state.localized.annualLeavePaymentRule,
  );

  const rule = employment?.details?.annualLeavePaymentRule || "";

  const defaultValues = useMemo(
    () => ({
      annualLeavePaymentRule: { id: rule, name: paymentRules[rule] },
    }),
    [rule, paymentRules],
  );

  const methods = useForm({
    defaultValues,
    mode: "onChange",
  });

  const {
    control,
    handleSubmit,
    reset,
    formState: { isDirty, errors },
  } = methods;


  useEffect(() => {
    reset(defaultValues);
  }, [rule, defaultValues, reset]);
  

  const onSubmit = (data: FieldValues) => {
    const payload = {
      employmentId: employment?.employmentId as string,
      version: employment?.details.version as number,
      annualLeavePaymentRule: data.annualLeavePaymentRule.id,
    };

    dispatch(editEmpAnnualLeavePaymentRule(payload)).then((action) => {
      if (isFulfilled(action)) {
        setEdit(false);
      }
    });
  };

  const isShift = employment?.isShift || false;

  const onSave = () => {
    if (!edit) setEdit(true);
    if (edit) handleSubmit(onSubmit)();
  };

  const onCancel = () => {
    setEdit(false);
  
    reset({
      annualLeavePaymentRule: {id: rule, name: paymentRules[rule]},
    });
  };

  const saveDisabled = !isDirty || !isEmpty(errors);

  const annualLeavePaymentRuleOptions = Object.keys(paymentRules).map((key) => ({
    id: key,
    name: paymentRules[key]
  }));

  return (
    <FormSection title={t("annualLeave")}
    edit={!isShift ? edit : undefined}
    saveDisabled={saveDisabled}
    onEdit={onSave}
    onCancel={onCancel}
    readOnly={!canEdit}
    >
      <>
        <Grid
          item
          container
          xs={2}
          justifyContent="flex-end"
          alignItems="center"
        >
          <Typography textAlign="end" variant="secondary">
            {t("annualLeavePaymentRule")}
          </Typography>
        </Grid>
        <Grid item xs={10}>
        <Controller
            name="annualLeavePaymentRule"
            control={control}
            render={({ field }) => 
              edit ? (
                <Autocomplete
                  {...field}
                  value={field.value}
                  options={annualLeavePaymentRuleOptions}
                  getOptionLabel={(option) => option.name}
                  isOptionEqualToValue={(option, value) => option.id === value.id}
                  onChange={(_,value) => field.onChange(value)}
                  disableClearable={true}         
                  renderInput={(params) => (
                    <CommonTextField
                    {...params}
                    sx={{ width: "30%", maxWidth: "300px" }}
                    ></CommonTextField>
                  )
                  }
                 />
              ) : (
                <Typography>{field.value.name}</Typography>
            )}
          />
        </Grid>
      </>
    </FormSection>
  );
};
